import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getSignupDraft,
    updateSignupDraft,
    setOTP,
} from "@/lib/storage";
import { sendOtp } from "@/lib/termii";
import { useAuth } from "@/context/AuthContext";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    TextField,
    PrimaryButton,
} from "@/screens/auth/_ui";
import CountryPhoneField from "@/components/CountryPhoneField";

const validators = {
    phone: (v) => {
        const s = v.replace(/\s+/g, "");
        if (!s) return "Phone number is required";
        if (!/^\+?[0-9]{7,15}$/.test(s)) return "Enter a valid phone number";
        return null;
    },
    email: (v) => {
        const s = v.trim();
        if (!s) return "Email is required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s)) return "Enter a valid email";
        return null;
    },
};

const SignupIdentifier = () => {
    const navigate = useNavigate();
    const { identifierIsTaken } = useAuth();
    const [draft, setDraft] = useState(null);
    const [value, setValue] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        const d = getSignupDraft();
        if (!d?.method) {
            navigate("/auth/signup", { replace: true });
            return;
        }
        setDraft(d);
    }, [navigate]);

    if (!draft) return null;

    const isPhone = draft.method === "phone";
    const label = isPhone ? "Phone number" : "Email address";
    const placeholder = isPhone ? "+234 800 000 0000" : "you@example.com";
    const inputMode = isPhone ? "tel" : "email";
    const autoComplete = isPhone ? "tel" : "email";
    const type = isPhone ? "tel" : "email";

    const handleContinue = async () => {
        const err = isPhone ? validators.phone(value) : validators.email(value);
        if (err) {
            setError(err);
            return;
        }
        if (identifierIsTaken(value)) {
            setError(
                isPhone
                    ? "This phone number is already registered"
                    : "This email is already registered"
            );
            return;
        }
        const trimmed = value.trim();
        const patch = isPhone ? { phone: trimmed } : { email: trimmed };
        updateSignupDraft(patch);

        // Email signups use Supabase's built-in email confirmation flow —
        // no custom OTP screen, no fake codes. Proceed straight to the
        // details step; Supabase will send the confirmation email after
        // signup completes (in `signup/Avatar.jsx`).
        if (!isPhone) {
            navigate("/auth/signup/details");
            return;
        }

        // Phone signups still use Termii SMS OTP.
        const result = await sendOtp({ identifier: trimmed, purpose: "signup", channel: "sms" });
        if (!result.ok) {
            setError(result.error || "Could not send code. Please try again.");
            return;
        }
        // Always seed the local store so OTPScreen's `purpose` guard passes
        // and so we know which pinId / identifier to verify against.
        setOTP({
            purpose: "signup",
            identifier: trimmed,
            pinId: result.pinId || null,
            sentAt: Date.now(),
        });
        navigate("/auth/signup/otp");
    };

    return (
        <AuthScreen testId="signup-identifier-screen">
            <ScreenHeader title="Create account" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[22px] font-semibold tracking-tight">
                        {isPhone ? "What's your phone number?" : "What's your email?"}
                    </h2>
                    <p className="text-[13.5px] text-muted-foreground">
                        {isPhone
                            ? "We'll send you a 6-digit code to confirm it."
                            : "We'll send a confirmation link after you finish signing up."}
                    </p>
                </div>

                <div className="mt-8 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    {isPhone ? (
                        <CountryPhoneField
                            testId="signup-identifier-phone"
                            value={value}
                            onChange={(v) => {
                                setValue(v);
                                if (error) setError(null);
                            }}
                            error={error}
                            autoFocus
                            label="Phone number"
                        />
                    ) : (
                        <TextField
                            testId="signup-identifier-input"
                            label={label}
                            placeholder={placeholder}
                            value={value}
                            onChange={(v) => {
                                setValue(v);
                                if (error) setError(null);
                            }}
                            type={type}
                            inputMode={inputMode}
                            autoComplete={autoComplete}
                            autoFocus
                            error={error}
                            onKeyDown={(e) => e.key === "Enter" && handleContinue()}
                        />
                    )}
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton testId="signup-identifier-continue" onClick={handleContinue}>
                    {isPhone ? "Send code" : "Continue"}
                </PrimaryButton>
            </AuthFooter>
        </AuthScreen>
    );
};

export default SignupIdentifier;
